import Phaser from 'phaser';
import { TEXT_RESOLUTION, platform } from '@platoon/client-kit';

export interface LevelInfo {
  name: string;
  hash: number; // of the level file; MSG_MATCH carries the room's, a mismatch triggers a live swap
  url: string;
  // Every image the map references (tileset sheets, prop images). Each is
  // loaded under its path, which is also how the map's tilesets name it.
  images: Array<{ path: string; url: string }>;
}

export const LEVEL_KEY = 'level';
// The classic tileset, loaded on every level: the camouflage props (brick,
// crate) are frames of it.
export const CAMO_SHEET_KEY = 'tileset.png';

// Queues a level's images that are not loaded yet.
export function loadLevelImages(scene: Phaser.Scene, info: LevelInfo): void {
  for (const img of info.images) if (!scene.textures.exists(img.path)) scene.load.image(img.path, img.url);
}

// Asks the server which level the room runs, loads its Tiled JSON and
// images, then hands over to the game scene.
export class BootScene extends Phaser.Scene {
  private label!: Phaser.GameObjects.Text;

  constructor() {
    super('boot');
  }

  create(): void {
    this.label = this.add.text(16, 16, 'loading level…', {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#d7e6d2',
      resolution: TEXT_RESOLUTION,
    });

    const room = platform.getRoomFromUrl();
    fetch('/api/level' + (room ? `?room=${encodeURIComponent(room)}` : ''))
      .then((r) => {
        if (!r.ok) throw new Error(`/api/level -> ${r.status}`);
        return r.json() as Promise<LevelInfo>;
      })
      .then((info) => {
        this.load.tilemapTiledJSON(LEVEL_KEY, info.url);
        loadLevelImages(this, info);
        if (!this.textures.exists(CAMO_SHEET_KEY)) this.load.image(CAMO_SHEET_KEY, `/api/levels/${CAMO_SHEET_KEY}`);
        this.load.once(Phaser.Loader.Events.COMPLETE, () => this.scene.start('game', info));
        this.load.once(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
          this.label.setText(`failed to load ${typeof file.url === 'string' ? file.url : file.key}`);
        });
        this.load.start();
      })
      .catch((err: unknown) => {
        this.label.setText(`cannot reach the game server\n${String(err)}`);
      });
  }
}
